import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View, useWindowDimensions } from 'react-native';

import { ProjectAvatar } from '@components/Avatar/ProjectAvatar';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { TaskPriority, useGetTaskQuery } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import {
  APP_STORE_URL,
  detectAppInstallation,
  isMobileBrowser,
} from '@utils/mobileDeepLink';

type TaskWelcomeScreenProps = {
  route: {
    params: {
      taskId: string;
      isPostRequest: boolean;
    };
  };
};

const formatShortDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  [TaskPriority.High]: theme.colors.alertRed,
  [TaskPriority.Medium]: theme.colors.orange,
  [TaskPriority.Low]: theme.colors.blue,
};

// ─── Local mobile components ──────────────────────────────────────────────────

const MobileAppHeader: React.FC = () => (
  <Box
    flexDirection='row'
    alignItems='center'
    justifyContent='space-between'
    style={{
      paddingHorizontal: theme.spacing.m,
      paddingVertical: theme.spacing.s,
      backgroundColor: theme.colors.white,
    }}>
    <Icon name='Logo' width={100} height={30} color='textPrimary' />
    <Box
      width={32}
      height={32}
      backgroundColor='grey02'
      alignItems='center'
      justifyContent='center'
      style={{ borderRadius: theme.borderRadii.m }}>
      <Icon name='User' width={18} height={18} color='grey05' />
    </Box>
  </Box>
);

type MobileTaskCardProps = {
  taskName: string;
  projectName?: string;
  projectIcon?: string;
  projectColor?: string;
  inviterName?: string;
  /** When provided, renders an icon inside the priority pill */
  priorityIconName?: string;
  priorityColor?: string;
  priorityLabel?: string;
  /** When true, renders a Calendar icon inside the date pill */
  showDateIcon?: boolean;
  dateLabel?: string | null;
  description?: string;
  usersCount?: number;
};

const MobileTaskCard: React.FC<MobileTaskCardProps> = ({
  taskName,
  projectName,
  projectIcon,
  projectColor,
  inviterName,
  priorityIconName,
  priorityColor,
  priorityLabel,
  showDateIcon,
  dateLabel,
  description,
  usersCount,
}) => {
  const { t } = useTranslation('models');
  const showPriorityPill = !!(priorityIconName || priorityColor);
  return (
    <Box
      backgroundColor='white'
      style={{
        borderRadius: theme.borderRadii.m,
        borderWidth: 1,
        borderColor: theme.colors.grey02,
      }}
      padding='m'
      gap='m'>
      {/* Top section: title + meta */}
      <Box backgroundColor='grey01' borderRadius='xs' padding='m' gap='m'>
        <Text variant='labelLarge' color='textPrimary'>
          {taskName}
        </Text>
        <Box flexDirection='row' alignItems='center' gap='m'>
          {!!projectName && (
            <Box flexDirection='row' alignItems='center' gap='xs'>
              {!!projectIcon && (
                <ProjectAvatar icon={projectIcon} color={projectColor} />
              )}
              <Text variant='metadata' color='textSecondary'>
                {projectName}
              </Text>
            </Box>
          )}
          {!!inviterName && (
            <Box flexDirection='row' alignItems='center' gap='xs'>
              <Box
                width={14}
                height={14}
                backgroundColor='greenDark'
                alignItems='center'
                justifyContent='center'
                style={{ borderRadius: theme.borderRadii.xxl }}>
                <Icon name='User' width={10} height={10} color='white' />
              </Box>
              <Text variant='metadata' color='textSecondary'>
                {inviterName}
              </Text>
            </Box>
          )}
        </Box>
      </Box>

      {/* Priority + date row */}
      {(showPriorityPill || !!dateLabel) && (
        <Box flexDirection='row' alignItems='center' gap='s' mb='xs'>
          {showPriorityPill && (
            <Box
              backgroundColor='white'
              paddingHorizontal='s'
              flexDirection='row'
              alignItems='center'
              justifyContent='center'
              gap='xs'
              style={{
                height: 28,
                borderRadius: theme.borderRadii.xxl,
                borderWidth: 1,
                borderColor: priorityColor ?? theme.colors.grey02,
              }}>
              {!!priorityIconName && (
                <Icon
                  name={priorityIconName as any}
                  width={12}
                  height={12}
                  color={priorityColor as any}
                />
              )}
              <Text variant='metadata' style={{ color: priorityColor }}>
                {priorityLabel}
              </Text>
            </Box>
          )}
          {!!dateLabel && (
            <Box
              backgroundColor='white'
              paddingHorizontal='s'
              flexDirection={showDateIcon ? 'row' : undefined}
              alignItems='center'
              justifyContent={showDateIcon ? undefined : 'center'}
              gap={showDateIcon ? 'xs' : undefined}
              style={{ height: 28, borderRadius: theme.borderRadii.xxl }}
              borderWidth={1}
              borderColor='grey02'>
              {showDateIcon && (
                <Icon
                  name='Calendar'
                  width={12}
                  height={12}
                  color='textSecondary'
                />
              )}
              <Text variant='metadata' color='textSecondary'>
                {dateLabel}
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* Description */}
      {!!description && (
        <Box gap='xs'>
          <Text variant='labelSmall' color='textPrimary'>
            {t('onBoarding.taskWelcome.description')}
          </Text>
          <Text variant='metadata' color='textSecondary'>
            {description}
          </Text>
        </Box>
      )}

      {/* Assigned to */}
      {!!usersCount && usersCount > 0 && (
        <Box gap='xs'>
          <Text variant='labelSmall' color='textPrimary'>
            {t('onBoarding.taskWelcome.assignedTo')}
          </Text>
          <Box flexDirection='row' alignItems='center' gap='xs'>
            <Icon name='Users' width={12} height={12} color='textSecondary' />
            <Text variant='metadata' color='textSecondary'>
              {t('onBoarding.taskWelcome.membersWorking', {
                count: usersCount,
              })}
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};

type MobileDownloadBannerProps = {
  title: string;
  body: string;
  downloadLabel: string;
  onPress: () => void;
};

const MobileDownloadBanner: React.FC<MobileDownloadBannerProps> = ({
  title,
  body,
  downloadLabel,
  onPress,
}) => (
  <Box
    padding='l'
    borderRadius='m'
    style={{ backgroundColor: theme.colors.black }}>
    <Text
      variant='labelEmphasized'
      color='white'
      style={{ marginBottom: theme.spacing.xxs }}>
      {title}
    </Text>
    <Text
      variant='webBodySecondary'
      style={{
        color: theme.colors.grey02,
        marginBottom: theme.spacing.m,
      }}>
      {body}
    </Text>
    <Button variant='greenSecondary' fullWidth onPress={onPress}>
      {downloadLabel}
    </Button>
  </Box>
);

type MobileBottomSheetProps = {
  open: boolean;
  windowHeight: number;
  onClose: () => void;
  children: React.ReactNode;
};

const MobileBottomSheet: React.FC<MobileBottomSheetProps> = ({
  open,
  windowHeight,
  onClose,
  children,
}) => {
  if (!open) return null;
  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: windowHeight,
        zIndex: 100,
        justifyContent: 'flex-end',
      }}>
      <Pressable
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.colors.grey06,
        }}
        onPress={onClose}
      />
      <View
        style={{
          backgroundColor: theme.colors.white,
          borderTopLeftRadius: theme.borderRadii.l,
          borderTopRightRadius: theme.borderRadii.l,
          paddingHorizontal: theme.spacing.m,
          paddingTop: theme.spacing.s,
          paddingBottom: theme.spacing.xl,
        }}>
        {/* Drag handle */}
        <View
          style={{
            alignItems: 'center',
            marginBottom: theme.spacing.m + theme.spacing.xxs,
          }}>
          <View
            style={{
              width: 40,
              height: 4,
              backgroundColor: theme.colors.grey03,
              borderRadius: theme.borderRadii.xxxs,
            }}
          />
        </View>
        {children}
      </View>
    </View>
  );
};

// ─── Main screen ──────────────────────────────────────────────────────────────

export const TaskWelcomeScreen: React.FC<TaskWelcomeScreenProps> = ({
  route,
}) => {
  const { taskId, isPostRequest } = route.params;
  const isMobile = isMobileBrowser();
  const { height: windowHeight } = useWindowDimensions();
  const { me } = useMe();
  const { t } = useTranslation('models');
  const appNavigation = useAppNavigation();

  const storedTaskName =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_invite_name') ?? ''
      : '';
  const storedInviterName =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_invite_inviter_name') ?? ''
      : '';
  const storedUsersCount =
    typeof window !== 'undefined'
      ? parseInt(
          window.sessionStorage.getItem('task_invite_users_count') ?? '0',
          10
        ) || 0
      : 0;
  const storedDescription =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_invite_description') ?? ''
      : '';
  const storedPriority =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_invite_priority') ?? ''
      : '';
  const storedStartDate =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_invite_start_date') ?? ''
      : '';
  const storedDueDate =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_invite_due_date') ?? ''
      : '';
  const storedProjectName =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_invite_project_name') ?? ''
      : '';
  const storedProjectIcon =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_invite_project_icon') ?? ''
      : '';

  const { data, loading } = useGetTaskQuery({
    variables: { id: taskId },
    skip: !taskId || isPostRequest,
    fetchPolicy: 'network-only',
  });

  const task = data?.getTask;
  const taskName = task?.name ?? storedTaskName;
  const projectName = task?.project?.name ?? '';
  const firstName = me?.firstName ?? me?.name?.split(' ')[0] ?? '';

  const [sheetOpen, setSheetOpen] = useState(true);

  const downloadAppCleanupRef = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      downloadAppCleanupRef.current?.();
    },
    []
  );

  const inviterName = storedInviterName || task?.creator?.name || '';

  if (loading && !storedTaskName) {
    return (
      <Box flex={1} alignItems='center' justifyContent='center'>
        <ActivityIndicatorLoading />
      </Box>
    );
  }

  const clearInviteSession = () => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem('task_invite_name');
    window.sessionStorage.removeItem('task_invite_inviter_name');
    window.sessionStorage.removeItem('task_invite_chat_id');
    window.sessionStorage.removeItem('task_invite_users_count');
    window.sessionStorage.removeItem('task_invite_description');
    window.sessionStorage.removeItem('task_invite_priority');
    window.sessionStorage.removeItem('task_invite_start_date');
    window.sessionStorage.removeItem('task_invite_due_date');
    window.sessionStorage.removeItem('task_invite_project_name');
    window.sessionStorage.removeItem('task_invite_project_icon');
  };

  const handleCta = () => {
    clearInviteSession();
    if (!isPostRequest && taskId) {
      // Navigate to tasks-stack with taskId param so Tasks.web.tsx (rendered
      // inside WebDrawerProvider) can open the task panel via its existing
      // routeTaskId useEffect. Calling navigateToTask directly here is
      // unreliable because TaskWelcomeScreen unmounts during navigation,
      // dropping the queued setIdTaskDetailOpen / setIsTaskWebPanelOpen
      // updates before they commit.
      appNavigation.navigate('tasks-stack', {
        screen: 'tasks',
        params: { taskId },
      } as any);
    } else {
      appNavigation.navigate('tasks-stack', {} as any);
    }
  };

  const ctaLabel = isPostRequest
    ? t('onBoarding.taskWelcome.browseTasks')
    : t('onBoarding.taskWelcome.goToTask');

  const handleDownloadApp = () => {
    downloadAppCleanupRef.current?.();
    downloadAppCleanupRef.current = detectAppInstallation(
      'com.eloveit.tasktag:///',
      APP_STORE_URL
    );
  };

  // ── Mobile post-request: task overview background + "Request Sent" bottom sheet ─
  if (isMobile && isPostRequest) {
    const priority = storedPriority as TaskPriority | '';
    const priorityColor = priority ? PRIORITY_COLOR[priority] : undefined;
    const priorityLabel = priority
      ? t(`invitationPreview.task.${priority.toLowerCase()}Priority`)
      : undefined;

    let dateLabel: string | null = null;
    if (storedStartDate && storedDueDate) {
      dateLabel = `Due: ${formatShortDate(storedStartDate)} - ${formatShortDate(
        storedDueDate
      )}`;
    } else if (storedDueDate) {
      dateLabel = `Due: ${formatShortDate(storedDueDate)}`;
    }

    return (
      <Box flex={1} backgroundColor='background' alignItems='center'>
        <Box
          flex={1}
          width='100%'
          style={{ maxWidth: 480 }}
          backgroundColor='grey01'>
          <MobileAppHeader />
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: theme.spacing.m,
              paddingBottom: 220,
              gap: theme.spacing.m,
              paddingTop: theme.spacing.m,
            }}>
            <MobileTaskCard
              taskName={storedTaskName}
              projectName={storedProjectName}
              projectIcon={storedProjectIcon}
              inviterName={storedInviterName}
              priorityColor={priorityColor}
              priorityLabel={priorityLabel}
              dateLabel={dateLabel}
              description={storedDescription}
              usersCount={storedUsersCount}
            />
            <MobileDownloadBanner
              title={t('onBoarding.taskWelcome.hangTight')}
              body={t('onBoarding.taskWelcome.pendingDownloadBody')}
              downloadLabel={t('onBoarding.teamWelcome.downloadTheApp')}
              onPress={handleDownloadApp}
            />
          </ScrollView>
          <MobileBottomSheet
            open={sheetOpen}
            windowHeight={windowHeight}
            onClose={() => setSheetOpen(false)}>
            {/* Clock icon + text */}
            <Box
              alignItems='center'
              style={{
                paddingVertical: theme.spacing.s,
                marginBottom: theme.spacing.l,
              }}>
              <Box
                width={56}
                height={56}
                backgroundColor='grey01'
                alignItems='center'
                justifyContent='center'
                mb='m'
                style={{ borderRadius: theme.borderRadii.xxl }}>
                <Icon name='Clock' width={28} height={28} color='grey04' />
              </Box>
              <Text variant='heading2' textAlign='center' mb='xs'>
                {t('projects.onboardingCard.requestSent')}
              </Text>
              <Text
                variant='webBodySecondary'
                color='textSecondary'
                textAlign='center'>
                {t('projects.onboardingCard.notifiedWhenApproved')}
              </Text>
            </Box>
            <Button
              variant='primary'
              fullWidth
              onPress={() => setSheetOpen(false)}
              accessibilityLabel={t('projects.onboardingCard.gotIt')}>
              {t('projects.onboardingCard.gotIt')}
            </Button>
          </MobileBottomSheet>
        </Box>
      </Box>
    );
  }

  // ── Mobile post-join: full task overview background + bottom sheet ─────────────
  if (isMobile && !isPostRequest) {
    const priority = task?.priority;
    const dueDate = task?.dueDate;
    const startDate = task?.startDate;
    const description = task?.description;
    const usersCount = task?.members?.length ?? storedUsersCount;
    const projectIcon = task?.project?.icon ?? '';
    const priorityColor = priority ? PRIORITY_COLOR[priority] : undefined;
    const priorityLabel = priority
      ? t(`invitationPreview.task.${priority.toLowerCase()}Priority`)
      : undefined;

    let dateLabel: string | null = null;
    if (startDate && dueDate) {
      dateLabel = `Due: ${formatShortDate(startDate)} - ${formatShortDate(
        dueDate
      )}`;
    } else if (dueDate) {
      dateLabel = `Due: ${formatShortDate(dueDate)}`;
    }

    return (
      <Box flex={1} backgroundColor='background' alignItems='center'>
        <Box
          flex={1}
          width='100%'
          style={{ maxWidth: 480 }}
          backgroundColor='grey01'>
          <MobileAppHeader />
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: theme.spacing.m,
              paddingBottom: 220,
              gap: theme.spacing.m,
              paddingTop: theme.spacing.m,
            }}>
            <MobileTaskCard
              taskName={taskName}
              projectName={projectName}
              projectIcon={projectIcon}
              projectColor={task?.project?.color ?? undefined}
              inviterName={inviterName}
              priorityColor={priorityColor}
              priorityLabel={priorityLabel}
              dateLabel={dateLabel}
              description={description ?? undefined}
              usersCount={usersCount}
            />
            <MobileDownloadBanner
              title={t('onBoarding.teamWelcome.youreAllSet')}
              body={t('onBoarding.teamWelcome.downloadAppBody')}
              downloadLabel={t('onBoarding.teamWelcome.downloadTheApp')}
              onPress={handleDownloadApp}
            />
          </ScrollView>
          <MobileBottomSheet
            open={sheetOpen}
            windowHeight={windowHeight}
            onClose={() => setSheetOpen(false)}>
            {/* Success icon */}
            <Box alignItems='center' mb='m'>
              <Box
                width={56}
                height={56}
                backgroundColor='greenSecondaryMild'
                alignItems='center'
                justifyContent='center'
                style={{ borderRadius: theme.borderRadii.xxl }}>
                <Icon
                  name='Check'
                  width={28}
                  height={28}
                  color='greenSecondary'
                />
              </Box>
            </Box>
            <Text variant='heading2' textAlign='center' mb='xs'>
              {t('onBoarding.taskWelcome.youveJoinedThisTask')}
            </Text>
            {!!taskName && (
              <Text
                variant='body'
                color='textSecondary'
                textAlign='center'
                mb='l'>
                {taskName}
              </Text>
            )}
            <Button
              variant='primary'
              fullWidth
              onPress={() => setSheetOpen(false)}
              accessibilityLabel={ctaLabel}>
              {ctaLabel}
            </Button>
          </MobileBottomSheet>
        </Box>
      </Box>
    );
  }

  // ── Post-request (pending approval) or mobile post-request ────────────────────
  const iconName = isPostRequest ? 'Clock' : 'Check';
  const iconBg = isPostRequest ? 'orangeMild' : 'greenSecondaryMild';
  const iconColor = isPostRequest ? 'orange' : 'greenSecondary';

  let heading: string;
  if (isPostRequest) {
    heading = t('onBoarding.taskWelcome.requestSent');
  } else if (firstName) {
    heading = t('onBoarding.taskWelcome.youJoinedWithName', { firstName });
  } else {
    heading = t('onBoarding.taskWelcome.youJoined');
  }

  let subheading: string;
  if (isPostRequest) {
    subheading = taskName
      ? t('onBoarding.taskWelcome.requestBodyWithName', { taskName })
      : t('onBoarding.taskWelcome.requestBody');
  } else {
    subheading = taskName
      ? t('onBoarding.taskWelcome.joinedBodyWithName', { taskName })
      : t('onBoarding.taskWelcome.joinedBody');
  }

  const steps = [
    t('onBoarding.taskWelcome.step1'),
    t('onBoarding.taskWelcome.step2'),
    t('onBoarding.taskWelcome.step3'),
  ];

  const contentMaxWidth = theme.spacing.xxl * 10;
  const scrollContentBottomPadding = theme.spacing.listFooter + theme.spacing.m;

  return (
    <Box flex={1} backgroundColor='grey01' alignItems='center'>
      <Box flex={1} width='100%' style={{ maxWidth: contentMaxWidth }}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingBottom: scrollContentBottomPadding,
          }}>
          {/* Header */}
          <Box
            backgroundColor='white'
            alignItems='center'
            paddingTop='xl'
            paddingBottom='xl'
            paddingHorizontal='l'
            style={{
              borderBottomLeftRadius: theme.borderRadii.l,
              borderBottomRightRadius: theme.borderRadii.l,
            }}>
            <Box
              width={72}
              height={72}
              backgroundColor={iconBg}
              alignItems='center'
              justifyContent='center'
              mb='s'
              style={{ borderRadius: theme.borderRadii.xxl }}>
              <Icon name={iconName} width={40} height={40} color={iconColor} />
            </Box>
            <Text
              variant='labelLarge'
              color='textPrimary'
              textAlign='center'
              mb='xxs'>
              {heading}
            </Text>
            <Text
              variant='webBodySecondary'
              color='textSecondary'
              textAlign='center'>
              {subheading}
            </Text>
          </Box>

          {/* Task info card */}
          {(taskName || projectName) && (
            <Box backgroundColor='white' borderRadius='m' margin='m' p='m'>
              {!!taskName && (
                <Text variant='labelEmphasized' color='textPrimary' mb='xxs'>
                  {taskName}
                </Text>
              )}
              {!!projectName && (
                <Text variant='webBodySecondary' color='grey05'>
                  {projectName}
                </Text>
              )}
            </Box>
          )}

          {/* What happens next — post-request only */}
          {isPostRequest && (
            <Box
              backgroundColor='white'
              borderRadius='m'
              marginHorizontal='m'
              p='m'>
              <Text variant='labelEmphasized' color='textPrimary' mb='s'>
                {t('onBoarding.taskWelcome.whatHappensNext')}
              </Text>
              {steps.map((step, i) => (
                <Box
                  key={i}
                  flexDirection='row'
                  alignItems='flex-start'
                  mb='s'
                  style={{ gap: theme.spacing.s }}>
                  <Box
                    width={24}
                    height={24}
                    backgroundColor='greenSecondaryMild'
                    alignItems='center'
                    justifyContent='center'
                    style={{ borderRadius: 12, flexShrink: 0 }}>
                    <Text variant='webSmall' color='greenSecondary'>
                      {i + 1}
                    </Text>
                  </Box>
                  <Text variant='webBodySecondary' color='textPrimary' flex={1}>
                    {step}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
        </ScrollView>

        {/* CTA — fixed at bottom */}
        <Box
          position='absolute'
          style={{
            bottom: theme.spacing.m,
            left: theme.spacing.m,
            right: theme.spacing.m,
          }}>
          <Button
            variant='primary'
            fullWidth
            onPress={handleCta}
            accessibilityLabel={ctaLabel}>
            {ctaLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
